import { useEffect, useMemo, useState } from "react";
import {
  X, Sparkles, Save, RefreshCw, Send, CheckCircle2, Download, Trash2,
  AlertTriangle, HelpCircle, Lightbulb, FileWarning, GitCompare, Clock,
  ShieldAlert, Loader2,
} from "lucide-react";
import toast from "react-hot-toast";
import PrdEditor from "./PrdEditor";
import { usePrdStore, type PrdStatus } from "../../store/usePrdStore";

// ── Review, edit and approve the PRD ─────────────────────────────────────────
//
// The document on the left, everything the AI is unsure about on the right.
// That split is the main design decision here and it is deliberate: the review
// summary is NOT in the document. Missing information, assumptions and
// recommendations are commentary on the requirements — a reader who cannot tell
// a recommendation from a requirement has been handed a worse document than no
// document, so they never share a column.

const STATUS_STYLES: Record<PrdStatus, string> = {
  Draft: "bg-slate-100 text-slate-700 border-slate-200",
  "In Review": "bg-amber-50 text-amber-800 border-amber-200",
  Approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Changes Required": "bg-rose-50 text-rose-800 border-rose-200",
  Outdated: "bg-orange-50 text-orange-800 border-orange-200",
};

const when = (d?: string | null) =>
  d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

const Panel = ({
  icon, title, tone = "neutral", count, children,
}: {
  icon: React.ReactNode;
  title: string;
  tone?: "neutral" | "warn" | "danger" | "info";
  count?: number;
  children: React.ReactNode;
}) => {
  const tones = {
    neutral: "border-outline-variant bg-surface-container-lowest",
    warn: "border-amber-200 bg-amber-50/60",
    danger: "border-rose-200 bg-rose-50/60",
    info: "border-sky-200 bg-sky-50/60",
  } as const;
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        {icon}
        <h5 className="text-[12px] font-bold text-on-surface">{title}</h5>
        {typeof count === "number" && (
          <span className="ml-auto text-[10px] font-bold text-on-surface-variant">{count}</span>
        )}
      </div>
      {children}
    </div>
  );
};

const Bullets = ({ items }: { items: string[] }) => (
  <ul className="space-y-1">
    {items.map((x, i) => (
      <li key={i} className="text-[11.5px] leading-snug text-on-surface-variant pl-3 relative">
        <span className="absolute left-0 top-[7px] w-1 h-1 rounded-full bg-current opacity-50" />
        {x}
      </li>
    ))}
  </ul>
);

interface Props {
  projectName: string;
  onClose: () => void;
}

const PrdWorkspaceModal = ({ projectName, onClose }: Props) => {
  const {
    prd, permissions, validation, conflicts, busy,
    save, regenerate, submit, approve, requestChanges, retryErp, republish, download, unlink,
  } = usePrdStore();

  const [html, setHtml] = useState(prd?.contentHtml || "");
  const [dirty, setDirty] = useState(false);
  // One confirmation at a time, each carrying the server's own sentence.
  const [confirmAction, setConfirmAction] = useState<null | {
    kind: "regenerate" | "approve" | "approve-incomplete" | "unapprove-edit" | "unlink";
    message: string;
    detail?: string;
  }>(null);
  const [changesNote, setChangesNote] = useState("");
  const [askingChanges, setAskingChanges] = useState(false);

  // Regeneration replaces the document underneath the editor. Pull it back in
  // unless the person has unsaved edits, which we would be throwing away.
  // `dirty` is a dependency on purpose. The store updates `prd` before the
  // save handler clears dirty, so without it this effect runs once while the
  // document is still marked dirty, skips the sync, and never runs again —
  // leaving the editor on the pre-save content.
  useEffect(() => {
    if (!prd) return;
    if (dirty) return;
    setHtml(prd.contentHtml || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prd?.contentHtml, prd?.updatedAt, dirty]);

  const review = prd?.aiReview;
  const reviewCount = useMemo(
    () =>
      (review?.missing?.length || 0) +
      (review?.conflicts?.length || 0) +
      (review?.assumptions?.length || 0) +
      (review?.unreadableSources?.length || 0),
    [review]
  );

  if (!prd || !permissions) return null;
  const working = Boolean(busy);

  // Returns true when the document on screen is safely stored. Callers that
  // need saved content — approving, submitting — chain off this rather than
  // refusing to run.
  const doSave = async (confirmUnapprove = false): Promise<boolean> => {
    const res = await save(prd._id, html, confirmUnapprove ? { confirmUnapprove: true } : {});
    if (res?.needsConfirm) {
      setConfirmAction({ kind: "unapprove-edit", message: res.needsConfirm });
      return false;
    }
    setDirty(false);
    return true;
  };

  // Same reasoning as approving: submitting sends what is stored, so anything
  // unsaved is saved first rather than the button simply refusing.
  const doSubmit = async () => {
    if (dirty) {
      const saved = await doSave(false);
      if (!saved) return;
    }
    await submit(prd._id);
  };

  const doRegenerate = async (confirm = false) => {
    const res = await regenerate(prd._id, confirm ? { confirm: true } : {});
    if (res?.needsConfirm) {
      setConfirmAction({
        kind: "regenerate",
        message: res.needsConfirm,
        detail: res.editedSections?.length ? `Your edits: ${res.editedSections.join(", ")}` : "",
      });
      return;
    }
    setDirty(false);
  };

  const doApprove = async (confirm = false, force = false) => {
    // Approval publishes what is SAVED, so unsaved edits have to land first.
    // Previously this refused and disabled the button, which left people
    // looking at an Approve they could not press and no way to find out why.
    // Saving on their behalf is what they meant by clicking it.
    if (dirty) {
      const saved = await doSave(false);
      if (!saved) return;
    }
    const res = await approve(prd._id, { ...(confirm ? { confirm: true } : {}), ...(force ? { force: true } : {}) });
    if (res?.needsConfirm) {
      const r = res.review || {};
      const flags = [
        r.missing ? `${r.missing} item(s) of missing information` : "",
        r.conflicts ? `${r.conflicts} conflict(s)` : "",
        r.assumptions ? `${r.assumptions} assumption(s)` : "",
        r.unreadableSources ? `${r.unreadableSources} unreadable source file(s)` : "",
      ].filter(Boolean);
      setConfirmAction({
        kind: "approve",
        message: res.needsConfirm,
        detail: flags.length ? `Still outstanding: ${flags.join(", ")}.` : "",
      });
      return;
    }
    // Required sections are still empty. That is worth stopping for once, but
    // it is the author's call — they may know the section genuinely has
    // nothing in it. Refusing outright left people with an Approve that could
    // never succeed and no way to say "yes, I know".
    if (res?.incomplete) {
      const empty = [...(res.incomplete.emptySections || []), ...(res.incomplete.missingSections || [])];
      setConfirmAction({
        kind: "approve-incomplete",
        message: `${empty.join(", ")} ${empty.length === 1 ? "is" : "are"} still empty. Approve anyway?`,
        detail: "Fill the section in or regenerate if you would rather not — the approved document is what the delivery team works from.",
      });
    }
  };

  const runConfirmed = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (!action) return;
    if (action.kind === "regenerate") return doRegenerate(true);
    if (action.kind === "approve") return doApprove(true);
    if (action.kind === "approve-incomplete") return doApprove(true, true);
    if (action.kind === "unapprove-edit") return doSave(true);
    if (action.kind === "unlink") {
      const gone = await unlink(prd._id);
      if (gone) onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-3 sm:p-6">
      <div
        className="bg-surface-container-lowest w-full max-w-6xl h-[92vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-3.5 border-b border-outline-variant shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-bold text-on-surface truncate">Project Requirements Document</h3>
              <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${STATUS_STYLES[prd.status]}`}>
                {prd.status}
              </span>
              {prd.aiReview?.generatedBy === "fallback" && (
                <span className="px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-800 text-[10px] font-bold">
                  Written without AI
                </span>
              )}
            </div>
            <p className="text-[11px] text-on-surface-variant mt-0.5 truncate">
              {projectName} · {prd.templateName}
              {prd.lastEditedByName ? ` · last edited by ${prd.lastEditedByName} ${when(prd.lastEditedAt)}` : ""}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-on-surface-variant hover:bg-surface-container rounded-full shrink-0">
            <X size={18} />
          </button>
        </div>

        {/* Banners — each one is a thing somebody has to decide about */}
        <div className="px-5 pt-3 space-y-2 shrink-0">
          {prd.status === "Outdated" && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2">
              <p className="text-[12px] font-semibold text-orange-900 flex items-center gap-1.5">
                <Clock size={13} /> The project has changed since this PRD was approved
              </p>
              <p className="text-[11px] text-orange-800 mt-0.5">
                What changed: {prd.outdatedReasons.join("; ")}. The approved document is unchanged and still available —
                regenerate only if the change affects the requirements.
              </p>
            </div>
          )}

          {prd.status === "Changes Required" && prd.changesRequestedNote && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
              <p className="text-[12px] font-semibold text-rose-900">Changes requested</p>
              <p className="text-[11px] text-rose-800 mt-0.5 whitespace-pre-wrap">{prd.changesRequestedNote}</p>
            </div>
          )}

          {prd.status === "Approved" && !prd.artifact && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 flex items-center justify-between gap-3">
              <p className="text-[11.5px] text-rose-900">
                <strong>Approved but not stored.</strong> The document could not be written to storage. The approval is kept — retry it.
              </p>
              <button type="button" onClick={() => republish(prd._id)} disabled={working}
                className="shrink-0 px-2.5 py-1 text-[11px] font-bold bg-rose-600 text-white rounded-lg disabled:opacity-50">
                Retry storing
              </button>
            </div>
          )}

          {prd.status === "Approved" && prd.artifact && prd.erp.status !== "Synced" && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex items-center justify-between gap-3">
              <p className="text-[11.5px] text-amber-900">
                {prd.erp.status === "Pending — No ERP Project"
                  ? "Stored and approved. It will be sent to ERP automatically once this project reaches ERP."
                  : `Stored and approved, but ERP has not accepted the reference yet${prd.erp.lastError ? `: ${prd.erp.lastError}` : "."}`}
              </p>
              {prd.erp.status !== "Pending — No ERP Project" && (
                <button type="button" onClick={() => retryErp(prd._id)} disabled={working}
                  className="shrink-0 px-2.5 py-1 text-[11px] font-bold bg-amber-600 text-white rounded-lg disabled:opacity-50">
                  Retry ERP
                </button>
              )}
            </div>
          )}

          {validation && !validation.ok && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              <p className="text-[11.5px] text-amber-900">
                {validation.emptySections.length > 0 && <>Required sections still empty: <strong>{validation.emptySections.join(", ")}</strong>. </>}
                {validation.missingSections.length > 0 && <>Removed from the template: <strong>{validation.missingSections.join(", ")}</strong>. </>}
                {validation.addedSections.length > 0 && <>Added by hand: <strong>{validation.addedSections.join(", ")}</strong>.</>}
              </p>
            </div>
          )}

          {conflicts.length > 0 && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2">
              <p className="text-[12px] font-semibold text-sky-900 flex items-center gap-1.5">
                <GitCompare size={13} /> {conflicts.length} of your edits differ from the regenerated content
              </p>
              <p className="text-[11px] text-sky-800 mt-0.5">
                Your version was kept in: {conflicts.map((c) => c.title).join(", ")}. Nothing was overwritten.
              </p>
            </div>
          )}

          {confirmAction && (
            <div className="rounded-lg border-2 border-primary bg-primary/5 px-3 py-2.5">
              <p className="text-[12px] font-semibold text-on-surface flex items-center gap-1.5">
                <ShieldAlert size={14} className="text-primary" /> Confirm
              </p>
              <p className="text-[11.5px] text-on-surface-variant mt-1">{confirmAction.message}</p>
              {confirmAction.detail && <p className="text-[11px] text-on-surface-variant mt-1 italic">{confirmAction.detail}</p>}
              <div className="flex gap-2 mt-2">
                <button type="button" onClick={runConfirmed} disabled={working}
                  className="px-3 py-1.5 text-[11px] font-bold bg-primary text-white rounded-lg disabled:opacity-50">
                  Yes, continue
                </button>
                <button type="button" onClick={() => setConfirmAction(null)}
                  className="px-3 py-1.5 text-[11px] font-semibold text-on-surface-variant hover:bg-surface-container rounded-lg">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 px-5 py-3 overflow-hidden">
          <div className="min-h-0 overflow-y-auto">
            <PrdEditor
              value={html}
              editable={permissions.edit && !working}
              onChange={(next) => { setHtml(next); setDirty(true); }}
            />
            {!permissions.edit && (
              <p className="text-[11px] text-on-surface-variant mt-2">
                You have read access to this document. Editing is done by the project owner or an administrator.
              </p>
            )}
          </div>

          {/* Review summary — commentary, never requirements */}
          <div className="min-h-0 overflow-y-auto space-y-2.5">
            <div className="rounded-xl border border-outline-variant bg-surface-container-low p-3">
              <p className="text-[11px] font-bold text-on-surface uppercase tracking-wide">AI review</p>
              <p className="text-[11px] text-on-surface-variant mt-1">
                {reviewCount === 0
                  ? "Nothing flagged. Read the document anyway — a clean review means the sources agreed, not that they were complete."
                  : `${reviewCount} thing(s) to look at before approving. None of this is part of the requirements.`}
              </p>
              {review?.generationError && (
                <p className="text-[10.5px] text-amber-800 mt-1.5">{review.generationError}</p>
              )}
            </div>

            {!!review?.unreadableSources?.length && (
              <Panel icon={<FileWarning size={13} className="text-rose-600" />} title="Files that could not be read" tone="danger" count={review.unreadableSources.length}>
                <ul className="space-y-1.5">
                  {review.unreadableSources.map((f, i) => (
                    <li key={i} className="text-[11.5px] leading-snug">
                      <span className="font-semibold text-on-surface">{f.name}</span>
                      <span className="block text-on-surface-variant">{f.reason}</span>
                    </li>
                  ))}
                </ul>
              </Panel>
            )}

            {!!review?.conflicts?.length && (
              <Panel icon={<AlertTriangle size={13} className="text-rose-600" />} title="Conflicting requirements" tone="danger" count={review.conflicts.length}>
                <ul className="space-y-1.5">
                  {review.conflicts.map((c, i) => (
                    <li key={i} className="text-[11.5px] leading-snug text-on-surface-variant">
                      {c.statement}
                      {!!c.sources?.length && <span className="block text-[10.5px] opacity-80">{c.sources.join(" vs ")}</span>}
                    </li>
                  ))}
                </ul>
              </Panel>
            )}

            {!!review?.missing?.length && (
              <Panel icon={<HelpCircle size={13} className="text-amber-600" />} title="Information still required" tone="warn" count={review.missing.length}>
                <Bullets items={review.missing} />
              </Panel>
            )}

            {!!review?.assumptions?.length && (
              <Panel icon={<HelpCircle size={13} className="text-sky-600" />} title="Assumptions to confirm" tone="info" count={review.assumptions.length}>
                <Bullets items={review.assumptions} />
              </Panel>
            )}

            {!!review?.recommendations?.length && (
              <Panel icon={<Lightbulb size={13} className="text-violet-600" />} title="Recommendations" count={review.recommendations.length}>
                <p className="text-[10.5px] text-on-surface-variant mb-1.5 italic">
                  Not agreed with the client and not part of the requirements.
                </p>
                <Bullets items={review.recommendations} />
              </Panel>
            )}

            {!!prd.requirements?.length && (
              <Panel icon={<CheckCircle2 size={13} className="text-emerald-600" />} title="Traced requirements" count={prd.requirements.length}>
                <p className="text-[10.5px] text-on-surface-variant">
                  {prd.requirements.filter((r) => r.kind === "functional").length} functional ·{" "}
                  {prd.requirements.filter((r) => r.kind === "non-functional").length} non-functional. Each names its source in the document.
                </p>
              </Panel>
            )}

            {!!prd.audit?.length && (
              <Panel icon={<Clock size={13} className="text-on-surface-variant" />} title="History">
                <ul className="space-y-1">
                  {prd.audit.slice(0, 8).map((a: any, i: number) => (
                    <li key={i} className="text-[11px] text-on-surface-variant leading-snug">
                      <span className="font-semibold text-on-surface capitalize">{String(a.action).replace(/-/g, " ")}</span>
                      {a.byName ? ` · ${a.byName}` : ""} · {when(a.at)}
                      {a.note ? <span className="block opacity-80">{a.note}</span> : null}
                    </li>
                  ))}
                </ul>
              </Panel>
            )}
          </div>
        </div>

        {/* Request changes */}
        {askingChanges && (
          <div className="px-5 pb-2 shrink-0">
            <div className="rounded-lg border border-outline-variant p-3">
              <label className="block text-[11px] font-semibold text-on-surface mb-1">
                What needs changing? The person who wrote it will see this.
              </label>
              <textarea
                autoFocus
                rows={2}
                value={changesNote}
                onChange={(e) => setChangesNote(e.target.value)}
                className="w-full px-3 py-2 border border-outline-variant rounded-lg text-[12px] bg-surface-container-lowest focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="e.g. The acceptance criteria for the booking flow are not specific enough to test against."
              />
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  disabled={!changesNote.trim() || working}
                  onClick={async () => {
                    const done = await requestChanges(prd._id, changesNote.trim());
                    if (done) { setAskingChanges(false); setChangesNote(""); }
                  }}
                  className="px-3 py-1.5 text-[11px] font-bold bg-rose-600 text-white rounded-lg disabled:opacity-50"
                >
                  Send back for changes
                </button>
                <button type="button" onClick={() => setAskingChanges(false)}
                  className="px-3 py-1.5 text-[11px] font-semibold text-on-surface-variant hover:bg-surface-container rounded-lg">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-outline-variant shrink-0 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {permissions.download && prd.artifact && (
              <button type="button" onClick={() => download(prd._id, prd.artifact!.fileName)} disabled={working}
                className="px-3 py-1.5 text-[11px] font-semibold text-on-surface bg-surface-container-low border border-outline-variant rounded-lg hover:bg-surface-container flex items-center gap-1.5 disabled:opacity-50">
                <Download size={13} /> Download
              </button>
            )}
            {permissions.unlink && (
              <button
                type="button"
                onClick={() =>
                  setConfirmAction({
                    kind: "unlink",
                    message: "Remove this PRD from the project? The stored document is deleted and this cannot be undone.",
                  })
                }
                disabled={working}
                className="px-3 py-1.5 text-[11px] font-semibold text-rose-700 hover:bg-rose-50 rounded-lg flex items-center gap-1.5 disabled:opacity-50"
              >
                <Trash2 size={13} /> Remove
              </button>
            )}
            {dirty && <span className="text-[11px] text-amber-700 font-semibold">Unsaved changes</span>}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {permissions.regenerate && (
              <button type="button" onClick={() => doRegenerate(false)} disabled={working}
                className="px-3 py-1.5 text-[11px] font-semibold text-on-surface bg-surface-container-low border border-outline-variant rounded-lg hover:bg-surface-container flex items-center gap-1.5 disabled:opacity-50">
                {busy === "regenerate" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Regenerate
              </button>
            )}
            {permissions.edit && (
              <button type="button" onClick={() => doSave(false)} disabled={working || !dirty}
                className="px-3 py-1.5 text-[11px] font-semibold text-on-surface bg-surface-container-low border border-outline-variant rounded-lg hover:bg-surface-container flex items-center gap-1.5 disabled:opacity-50">
                {busy === "save" ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save draft
              </button>
            )}
            {permissions.requestChanges && !askingChanges && (
              <button type="button" onClick={() => setAskingChanges(true)} disabled={working}
                className="px-3 py-1.5 text-[11px] font-semibold text-rose-700 border border-rose-200 bg-rose-50 rounded-lg hover:bg-rose-100 disabled:opacity-50">
                Request changes
              </button>
            )}
            {/* Only shown to someone who CANNOT approve it themselves, plus as
                a quiet secondary for anyone who deliberately wants a second
                opinion. The BD approves their own PRD, so putting "Submit for
                approval" in front of them would be inviting a wait that the
                workflow does not require. */}
            {permissions.submit && !permissions.approve && (
              <button type="button" onClick={doSubmit} disabled={working}
                title={dirty ? "Your unsaved changes are saved first, then submitted" : ""}
                className="px-3.5 py-1.5 text-[11px] font-bold text-white bg-primary rounded-lg hover:brightness-110 flex items-center gap-1.5 disabled:opacity-50">
                {busy === "submit" || busy === "save" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                {dirty ? "Save & submit" : "Submit for approval"}
              </button>
            )}
            {permissions.submit && permissions.approve && (
              <button type="button" onClick={doSubmit} disabled={working}
                title="Optional — sends it to an administrator for a second opinion instead of approving it yourself"
                className="px-3 py-1.5 text-[11px] font-semibold text-on-surface-variant hover:bg-surface-container rounded-lg flex items-center gap-1.5 disabled:opacity-50">
                {busy === "submit" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Ask for a review
              </button>
            )}
            {permissions.approve && (
              <button type="button" onClick={() => doApprove(false)} disabled={working}
                title={dirty ? "Your unsaved changes are saved first, then approved" : ""}
                className="px-3.5 py-1.5 text-[11px] font-bold text-white bg-emerald-600 rounded-lg hover:brightness-110 flex items-center gap-1.5 disabled:opacity-50">
                {busy === "approve" || busy === "save" ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                {prd.status === "Outdated" ? "Re-approve as still valid" : dirty ? "Save & approve" : "Approve"}
              </button>
            )}
            {/* Said out loud when no action is available. A row of missing
                buttons looks like a broken screen; it is usually a permission,
                and the person has no way to tell which. */}
            {!permissions.approve && !permissions.edit && !permissions.submit && (
              <span className="text-[11px] text-on-surface-variant">
                {prd.status === "Approved"
                  ? "Approved — edit it to make changes."
                  : "You can read this PRD but not change it."}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrdWorkspaceModal;
